import { isAmericanoFormat } from "./methods";

/**
 * How a category takes sign-ups (spec 2026-10-29). "both" is an individual-mode category (capacity
 * in players, a solo pool) that also accepts complete teams, so every existing
 * allowIndividualRegistration branch keeps its meaning.
 */
export type RegistrationMode = "teams" | "individuals" | "both";

/** The team that holds solo players until the admin pairs them (TeamNames.IndividualRegistration). */
export const POOL_TEAM_NAME = "__INDIVIDUAL_REGISTRATION__";

export const BOTH_SOLO_FORMAT_ERROR = "Players play alone in this format, so it can't take teams too.";
export const BOTH_TEAM_SIZE_ERROR = "Set how many players are in each team (at least 2).";
export const BOTH_TEAM_MIN_SIZE_ERROR = "Minimum players per team must be between 2 and the team size.";
export const BOTH_PER_PLAYER_FEE_ERROR = "In a category open to teams and individuals, fees are per player.";
export const BOTH_TEAM_MIX_ERROR =
  "Max men and women per team must each be between 0 and the team size, and add up to at least the team size.";
export const BOTH_GENDER_ERROR = "Choose who can play in this category: men, women or mixed.";

export const registrationModeOf = (allowIndividual?: boolean, allowTeam?: boolean): RegistrationMode =>
  !allowIndividual ? "teams" : allowTeam ? "both" : "individuals";

export const modeFlags = (
  mode: RegistrationMode
): { allowIndividualRegistration: boolean; allowTeamRegistration: boolean } => ({
  allowIndividualRegistration: mode !== "teams",
  allowTeamRegistration: mode === "both",
});

/** The team size an Americano-family format fixes (its unit size); null when the admin sets it. */
export const fixedTeamSize = (formatType: string | undefined, unitSize: number | undefined): number | null =>
  isAmericanoFormat(formatType) ? unitSize || 1 : null;

const isWholeIn = (value: number, max: number) => Number.isInteger(value) && value >= 0 && value <= max;

/**
 * Mirrors the backend's RegistrationModeRules.ValidateCategorySettings for a Both category (same
 * order, same copy): null when the settings can be saved, else the reason. teamMinSize null = teams
 * have exactly the team size (spec 2026-10-30 §1).
 */
export const bothSettingsError = (
  fixedSize: number | null,
  s: {
    teamSize?: number | null;
    teamMinSize?: number | null;
    teamMaxMen?: number | null;
    teamMaxWomen?: number | null;
    fees: { perTeam: boolean; isEnabled?: boolean }[];
  }
): string | null => {
  if (fixedSize === 1) return BOTH_SOLO_FORMAT_ERROR;
  const size = fixedSize ?? s.teamSize;
  if (size == null || !Number.isInteger(size) || size < 2) return BOTH_TEAM_SIZE_ERROR;
  const min = s.teamMinSize ?? null;
  if (min != null && (!Number.isInteger(min) || min < 2 || min > size)) return BOTH_TEAM_MIN_SIZE_ERROR;
  const men = s.teamMaxMen ?? null;
  const women = s.teamMaxWomen ?? null;
  if (men != null && !isWholeIn(men, size)) return BOTH_TEAM_MIX_ERROR;
  if (women != null && !isWholeIn(women, size)) return BOTH_TEAM_MIX_ERROR;
  if (men != null && women != null && men + women < size) return BOTH_TEAM_MIX_ERROR;
  if (s.fees.some((f) => f.perTeam && f.isEnabled !== false)) return BOTH_PER_PLAYER_FEE_ERROR;
  return null;
};

/**
 * An individual category keeps its gender in maxMen/maxWomen (one side 0 = single-gender, both > 0 =
 * mixed); 0 and 0 lets no one in, which the server refuses for a Both category. Null when someone can
 * play. Takes the edit drawer's typed strings too (blank counts as 0).
 */
export const bothGenderError = (
  maxMen: number | string | null | undefined,
  maxWomen: number | string | null | undefined
): string | null => (Number(maxMen) > 0 || Number(maxWomen) > 0 ? null : BOTH_GENDER_ERROR);

/** Players still waiting in the solo pool (the only ones Divide players accepts). */
export const poolPlayers = <T extends { teamName?: string }>(players: T[]): T[] =>
  players.filter((p) => p.teamName === POOL_TEAM_NAME);

/** The minimums a Both category offers: 2 up to the team size (the size itself = exactly that many). */
export const teamMinSizeOptions = (teamSize: number | null | undefined): number[] =>
  teamSize != null && teamSize >= 2 ? Array.from({ length: teamSize - 1 }, (_, i) => i + 2) : [];

/**
 * The minimum a form sends: null when it's the team size, so an exact category stays exact when the size
 * changes later (the server reads null back as the size).
 */
export const storedTeamMinSize = (
  teamMinSize: number | null | undefined,
  teamSize: number | null | undefined
): number | null => (teamMinSize == null || teamMinSize === teamSize ? null : teamMinSize);

/**
 * Adding a team to a Both category: it needs from the minimum (default: the team size) up to the team
 * size players (no team size, no block).
 */
export const teamConfirmBlocked = (count: number, teamSize?: number, minSize?: number): boolean =>
  teamSize != null && (count > teamSize || count < (minSize ?? teamSize));

/** The line under a Both team's members while it can't be added yet. */
export const teamSizeHint = (teamSize: number, minSize?: number): string =>
  minSize != null && minSize < teamSize ? `Add ${minSize} to ${teamSize} players.` : `Add exactly ${teamSize} players.`;

/**
 * "2/3 players" for a premade team in a Both category that still needs players (a short team plays once
 * the admin moves a waiting solo player in); null for a full team and for the solo pool.
 */
export const shortTeamLabel = (
  teamName: string | undefined,
  memberCount: number,
  teamSize: number | null | undefined
): string | null =>
  teamSize != null && teamName !== POOL_TEAM_NAME && memberCount < teamSize ? `${memberCount}/${teamSize} players` : null;

/** The whole-category caps a Mixed category starts with: 100 men and 100 women, i.e. no limit. */
export const NO_CATEGORY_GENDER_LIMIT = 100;

/**
 * A mixed Both category caps men and women across the whole category (all teams and solo players) when
 * either whole-category cap is below the Mixed default. Takes the edit drawer's typed strings too.
 */
export const hasCategoryGenderLimit = (
  maxMen: number | string | null | undefined,
  maxWomen: number | string | null | undefined
): boolean => Number(maxMen) < NO_CATEGORY_GENDER_LIMIT || Number(maxWomen) < NO_CATEGORY_GENDER_LIMIT;

const people = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "at most 2 men and 2 women", "no men and at most 3 women", "at most 2 men, any number of women"… */
const capsPhrase = (men: number | null, women: number | null): string => {
  if (men == null && women == null) return "any mix of men and women";
  if (men != null && women != null && men > 0 && women > 0)
    return `at most ${people(men, "man", "men")} and ${people(women, "woman", "women")}`;
  const cap = (n: number, one: string, many: string) => (n === 0 ? `no ${many}` : `at most ${people(n, one, many)}`);
  if (men == null) return `${cap(women!, "woman", "women")}, any number of men`;
  if (women == null) return `${cap(men, "man", "men")}, any number of women`;
  return `${cap(men, "man", "men")} and ${cap(women, "woman", "women")}`;
};

/**
 * The plain line under a mixed Both category's gender settings: what each team may have, then the
 * whole-category caps (or that there are none).
 */
export const bothGenderSummary = (s: {
  teamMaxMen?: number | null;
  teamMaxWomen?: number | null;
  limitCategory: boolean;
  maxMen?: number | string | null;
  maxWomen?: number | string | null;
}): string => {
  const team = `Each team: ${capsPhrase(s.teamMaxMen ?? null, s.teamMaxWomen ?? null)}.`;
  const category = s.limitCategory
    ? `Whole category: ${capsPhrase(Number(s.maxMen) || 0, Number(s.maxWomen) || 0)} in total.`
    : "No category-wide gender limit.";
  return `${team} ${category}`;
};
